import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ChevronLeft, Info, Loader2 } from 'lucide-react';
import { PageTransition } from '../components/PageTransition';
import { QuantityStepper } from '../components/QuantityStepper';
import { useOrder } from '../context/OrderContext';
import { useLanguage } from '../context/LanguageContext';
import { calculateLineTotal, calculateRollsTotal, getTotalRollQuantity, ROLL_UNIT_PRICE, submitOrder, CATEGORIES } from '../data/menu';
export const Cart = () => {
  const navigate = useNavigate();
  const {
    t,
    language,
    dateLocale
  } = useLanguage();
  const {
    cart,
    pickupDate,
    updateQuantity,
    cartTotal,
    cartCount,
    notes,
    setNotes,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    phone,
    setPhone
  } = useOrder();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!pickupDate || cart.length === 0) {
      navigate('/menu');
    }
  }, [pickupDate, cart.length, navigate]);
  if (!pickupDate || cart.length === 0) return null;
  const handleConfirm = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError(t('cart.error_name'));
      return;
    }
    if (!phone.trim()) {
      setError(t('cart.error_phone'));
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const orderNumber = Math.floor(10000 + Math.random() * 90000).toString();
      const itemsString = cart.map(c => `${c.quantity}× ${c.item.name}`).join(', ');
      const categoryString = Array.from(new Set(cart.map(c => {
        const cat = CATEGORIES.find(cat => cat.id === c.item.categoryId);
        return cat ? cat.name : c.item.categoryId;
      }))).join(', ');
      await submitOrder({
        order_number: orderNumber,
        created_at: format(new Date(), 'dd / MM'),
        pickup_date: format(pickupDate, 'dd / MM'),
        customer_name: `${firstName.trim()} ${lastName.trim()}`,
        phone: phone.trim(),
        notes: notes,
        items: itemsString,
        category: categoryString,
        item_count: cartCount,
        total: cartTotal,
        status: 'Not done',
        TN: '',
        EC: ''
      });
      navigate('/confirmation', {
        state: {
          orderNumber
        }
      });
    } catch (err) {
      setError(t('cart.error_submit'));
    } finally {
      setIsSubmitting(false);
    }
  };
  return <PageTransition className="bg-white min-h-screen" data-id="element-55">
      {/* Header */}
      <div className="sticky top-0 bg-white/80 backdrop-blur-md z-10 border-b border-border/50 px-4 py-4 flex items-center justify-between" data-id="element-56">
        <button onClick={() => navigate('/menu')} className="w-10 h-10 bg-cream rounded-full flex items-center justify-center text-espresso hover:bg-cream-dark transition-colors" data-id="element-57">
          <ChevronLeft size={20} data-id="element-58" />
        </button>
        <h1 className="font-serif text-xl text-espresso" data-id="element-59">{t('cart.header')}</h1>
        <div className="w-10" data-id="element-60" /> {/* Spacer */}
      </div>

      <div className="p-4 pb-32" data-id="element-61">
        {/* Pickup Info */}
        <div className="bg-cream rounded-2xl p-4 mb-6 border border-border/50" data-id="element-62">
          <div className="text-sm text-espresso-light mb-1" data-id="element-63">
            {t('cart.pickup_date')}
          </div>
          <div className="font-serif text-lg text-terracotta-dark capitalize" data-id="element-64">
            {format(pickupDate, 'EEEE, MMMM do', {
            locale: dateLocale
          })}
          </div>
          <div className="text-sm text-espresso mt-2 flex items-start gap-2" data-id="element-65">
            <Info size={16} className="text-terracotta mt-0.5 shrink-0" data-id="element-66" />
            <span data-id="element-67">{t('cart.pickup_info')}</span>
          </div>
        </div>

        {/* Order Items */}
        <h2 className="font-serif text-lg text-espresso mb-4" data-id="element-68">
          {t('cart.items')}
        </h2>
        <div className="space-y-4 mb-4" data-id="element-69">
          {cart.map(cartItem => {
          const lineTotal = calculateLineTotal(cartItem.item, cartItem.quantity);
          const itemName = language === 'en' ? cartItem.item.nameEn : cartItem.item.name;
          return <div key={cartItem.item.id} className="flex items-start justify-between gap-4 py-2 border-b border-border/40 last:border-0" data-id="element-70">
                <div className="flex-1" data-id="element-71">
                  <div className="font-medium text-espresso" data-id="element-72">{itemName}</div>
                  <div className="text-sm text-espresso-light mt-1" data-id="element-73">
                    {cartItem.item.price} {t('cart.each')}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2" data-id="element-74">
                  <div className="font-serif text-lg" data-id="element-75">{lineTotal} kr</div>
                  <QuantityStepper quantity={cartItem.quantity} onChange={qty => updateQuantity(cartItem.item.id, qty)} data-id="element-76" />
                </div>
              </div>;
        })}
        </div>

        {/* Rolls bundle deal — applies across mixed roll types */}
        {(() => {
        const rollQty = getTotalRollQuantity(cart);
        const baseRollCost = rollQty * ROLL_UNIT_PRICE;
        const dealRollCost = calculateRollsTotal(rollQty);
        const savings = baseRollCost - dealRollCost;
        if (savings <= 0) return null;
        return <div className="mb-8 -mt-1 bg-terracotta/10 text-terracotta-dark rounded-xl px-4 py-3 flex items-center justify-between gap-3" data-id="element-77">
              <span className="text-sm font-medium" data-id="element-78">
                {t('cart.rolls_deal')}
              </span>
              <span className="font-serif text-base whitespace-nowrap" data-id="element-79">
                −{savings} kr
              </span>
            </div>;
      })()}

        {/* First name */}
        <div className="mb-4" data-id="element-80">
          <label htmlFor="first-name" className="block font-serif text-lg text-espresso mb-2" data-id="element-81">
            {t('cart.firstname_label')}
          </label>
          <input id="first-name" type="text" autoComplete="given-name" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder={t('cart.firstname_placeholder')} className="w-full bg-cream border border-border/50 rounded-xl p-3 text-espresso placeholder:text-espresso-light/60 focus:outline-none focus:ring-2 focus:ring-terracotta/50" data-id="element-82" />
        </div>

        {/* Last name */}
        <div className="mb-4" data-id="element-83">
          <label htmlFor="last-name" className="block font-serif text-lg text-espresso mb-2" data-id="element-84">
            {t('cart.lastname_label')}
          </label>
          <input id="last-name" type="text" autoComplete="family-name" value={lastName} onChange={e => setLastName(e.target.value)} placeholder={t('cart.lastname_placeholder')} className="w-full bg-cream border border-border/50 rounded-xl p-3 text-espresso placeholder:text-espresso-light/60 focus:outline-none focus:ring-2 focus:ring-terracotta/50" data-id="element-85" />
        </div>

        {/* Phone */}
        <div className="mb-6" data-id="element-86">
          <label htmlFor="phone" className="block font-serif text-lg text-espresso mb-2" data-id="element-87">
            {t('cart.phone_label')}
          </label>
          <input id="phone" type="tel" inputMode="tel" autoComplete="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder={t('cart.phone_placeholder')} className="w-full bg-cream border border-border/50 rounded-xl p-3 text-espresso placeholder:text-espresso-light/60 focus:outline-none focus:ring-2 focus:ring-terracotta/50" data-id="element-88" />
        </div>

        {/* Notes */}
        <div className="mb-8" data-id="element-89">
          <label htmlFor="notes" className="block font-serif text-lg text-espresso mb-2" data-id="element-90">
            {t('cart.notes_label')}
          </label>
          <textarea id="notes" value={notes} onChange={e => setNotes(e.target.value)} placeholder={t('cart.notes_placeholder')} className="w-full bg-cream border border-border/50 rounded-xl p-3 text-espresso placeholder:text-espresso-light/60 focus:outline-none focus:ring-2 focus:ring-terracotta/50 resize-none h-24" data-id="element-91" />
        </div>
      </div>

      {/* Footer Checkout */}
      <div className="fixed bottom-0 left-0 right-0 z-50 flex justify-center pb-6 pt-4 px-4 bg-white border-t border-border/50" data-id="element-92">
        <div className="w-full max-w-md" data-id="element-93">
          {error && <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm rounded-xl border border-red-100 text-center" data-id="element-94">
              {error}
            </div>}
          <div className="flex justify-between items-center mb-3 px-2" data-id="element-95">
            <span className="text-espresso-light font-medium" data-id="element-96">
              {t('cart.total')}
            </span>
            <span className="font-serif text-2xl text-espresso" data-id="element-97">
              {cartTotal} kr
            </span>
          </div>
          <p className="text-xs text-espresso-light text-center mb-4 px-2 flex items-start justify-center gap-1.5" data-id="element-98">
            <Info size={14} className="text-terracotta mt-0.5 shrink-0" data-id="element-99" />
            <span data-id="element-100">{t('cart.payment_info')}</span>
          </p>
          <button onClick={handleConfirm} disabled={isSubmitting || !firstName.trim() || !lastName.trim() || !phone.trim()} className="w-full bg-espresso hover:bg-espresso-light disabled:bg-espresso/50 text-white rounded-2xl p-4 font-medium text-lg transition-colors active:scale-[0.98] flex justify-center items-center gap-2" data-id="element-101">
            {isSubmitting ? <>
                <Loader2 size={20} className="animate-spin" data-id="element-102" />
                {t('cart.submitting')}
              </> : t('cart.confirm')}
          </button>
        </div>
      </div>
    </PageTransition>;
};